import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

// The box in the footer where someone without a login can ask for emails about songs.
// They type their address, get one email, and are on the list only after pressing the button in it.
// People who are signed in do not see it: their own switch is on the Account page.
export default function NewsletterSignup() {
  const { user, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [trap, setTrap] = useState("");     // a hidden box that people never fill in; bots do
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  if (!ready || user) return null;

  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/mail/subscribe", { email: email.trim(), website: trap });
      setDone(data?.message || "Thank you. Check your email to finish signing up.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We could not sign you up just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="newsletter-signup" aria-labelledby="newsletter-title" className="max-w-xl mb-8 text-zinc-300">
      <h2 id="newsletter-title" className="font-display text-lg font-bold text-white">Get new songs by email</h2>
      <p className="text-sm text-zinc-400 mt-1">New songs, featured songs and a monthly roundup. No login needed.</p>
      {done ? (
        <div data-testid="newsletter-done" role="status" className="mt-3 text-sm text-cyan-200">
          <p>{done}</p>
          <button type="button" onClick={() => setDone("")} className="mt-2 text-xs text-zinc-400 underline hover:text-zinc-200">Use another address</button>
        </div>
      ) : (
        <form onSubmit={send} noValidate className="mt-3">
          <div className="flex flex-wrap gap-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" aria-label="Your email address"
              autoComplete="email" inputMode="email" data-testid="newsletter-email"
              className="flex-1 min-w-[12rem] bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-base sm:text-sm text-white focus:outline-none focus:border-cyan-400/50" />
            <input type="text" name="website" value={trap} onChange={(e) => setTrap(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <button type="submit" disabled={busy || !email.trim()} data-testid="newsletter-send"
              className="px-5 py-2.5 rounded-full bg-cyan-400 text-black text-sm font-semibold hover:bg-cyan-300 disabled:opacity-60">{busy ? "Sending..." : "Sign me up"}</button>
          </div>
          {error && <p data-testid="newsletter-error" role="alert" className="mt-2 text-sm text-red-300">{error}</p>}
          <p className="text-xs text-zinc-500 mt-2">
            We send one email first, to make sure it is you. Every email has an unsubscribe link. Never sold or shared for ads.{" "}
            <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy</Link>
          </p>
        </form>
      )}
    </section>
  );
}
